import { useMemo } from 'react'

import { useSelector } from 'react-redux'

import { makeSelectUser } from '@webapp/core/stores/global/selectors'
import { mapLegalEntitySummaryWithBaseCurrencyApiModel } from '@webapp/platform/legal-entities/data-access/mappers/mapLegalEntitySummaryWithBaseCurrencyApiModel'
import { LegalEntitySummaryWithBaseCurrency } from '@webapp/platform/legal-entities/entities/LegalEntitySummaryWithBaseCurrency'

export interface IUseUserLegalEntitiesReturn {
  legalEntities: LegalEntitySummaryWithBaseCurrency[]
  defaultLegalEntity: Nullable<LegalEntitySummaryWithBaseCurrency>
}

/**
 * The current user's assigned Legal Entities from `/global`, and the one host
 * pages should preselect: the home Location's entity when the user is assigned
 * to it, otherwise the first by name.
 */
export const useUserLegalEntities = (): IUseUserLegalEntitiesReturn => {
  const user = useSelector(makeSelectUser())
  const assignedLegalEntities = user?.legal_entities
  const homeLegalEntityUuid = user?.department?.branch?.legal_entity?.uuid

  const legalEntities = useMemo(
    () =>
      (assignedLegalEntities ?? [])
        .map(
          (legalEntity) =>
            new LegalEntitySummaryWithBaseCurrency(
              mapLegalEntitySummaryWithBaseCurrencyApiModel(legalEntity)
            )
        )
        .sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        ),
    [assignedLegalEntities]
  )

  const defaultLegalEntity = useMemo(
    () =>
      legalEntities.find(
        (legalEntity) => legalEntity.uuid === homeLegalEntityUuid
      ) ??
      legalEntities[0] ??
      null,
    [legalEntities, homeLegalEntityUuid]
  )

  return { legalEntities, defaultLegalEntity }
}
